import React, { useState, useEffect } from 'react';
import { __ } from './translations';

const MissingContributions = () => {
    const [data, setData] = useState({ years: [], companies: [] });
    const [selectedYear, setSelectedYear] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reminderStatus, setReminderStatus] = useState({});
    const [sendingBulkReminders, setSendingBulkReminders] = useState(false);

    useEffect(() => {
        fetch(`${boardMemberData.restURL}board-member/v1/missing-contributions`, {
            headers: {
                'X-WP-Nonce': boardMemberData.nonce,
            },
        })
            .then(response => response.json())
            .then(responseData => {
                console.log('Fetched data:', responseData);
                setData(responseData);
                if (responseData.years && responseData.years.length > 0) {
                    const currentYear = new Date().getFullYear().toString();
                    const defaultYear = responseData.years.includes(parseInt(currentYear)) ? 
                        currentYear : responseData.years[0].toString();
                    setSelectedYear(defaultYear);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                setError(__('Failed to load missing contributions.'));
                setLoading(false);
            });
    }, []);

    const sendReminder = async (userId, year, companyName) => {
        try {
            console.log(`Sending reminder - User: ${userId}, Year: ${year}, Company: ${companyName}`);
            
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/send-reminder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce,
                },
                body: JSON.stringify({ 
                    user_id: parseInt(userId),
                    year: year.toString()
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                console.error('Reminder API error:', data);
                throw new Error(data.message || 'Failed to send reminder');
            }
    
            const key = `${userId}-${year}`;
            setReminderStatus(prevStatus => ({
                ...prevStatus,
                [key]: {
                    status: 'success',
                    message: __('Reminder sent!')
                }
            }));
    
            return true;
    
        } catch (error) {
            console.error('Error sending reminder:', error);
            const key = `${userId}-${year}`;
            setReminderStatus(prevStatus => ({
                ...prevStatus,
                [key]: {
                    status: 'error',
                    message: error.message || __('Failed to send reminder')
                }
            }));
    
            return false;
        }
    };
    
    const sendBulkReminders = async () => {
        if (!window.confirm(__('Send reminders to all users from companies missing contributions for year ') + selectedYear + '?')) {
            return;
        }
    
        setSendingBulkReminders(true);
        let successCount = 0;
        let failCount = 0;
    
        try {
            const filteredCompanies = data.companies.filter(company => 
                company.missing_years.includes(parseInt(selectedYear))
            );
    
            for (const company of filteredCompanies) {
                console.log(`Processing company: ${company.company_name}`);
                for (const user of company.users) {
                    const success = await sendReminder(user.ID, selectedYear, company.company_name);
                    if (success) {
                        successCount++;
                    } else {
                        failCount++;
                    }
                    // Add a small delay between requests to avoid overwhelming the server
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        } finally {
            setSendingBulkReminders(false);
            alert(`Reminders sent: ${successCount} successful, ${failCount} failed`);
        }
    };

    if (loading) {
        return <p>{__('Loading...')}</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    const filteredCompanies = data.companies.filter(company => 
        company.missing_years.includes(parseInt(selectedYear))
    );

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2>{__('Missing Contributions')}</h2>
                <div className="flex gap-4">
                    <select 
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg p-2.5"
                        value={selectedYear || ''}
                        onChange={(e) => setSelectedYear(e.target.value)}
                    >
                        {data.years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                    {filteredCompanies.length > 0 && (
                        <button
                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                            onClick={sendBulkReminders}
                            disabled={sendingBulkReminders}
                        >
                            {sendingBulkReminders ? 
                                __('Sending Reminders...') : 
                                __('Send Reminders to All')}
                        </button>
                    )}
                </div>
            </div>

            {filteredCompanies.length > 0 ? (
                <div className="space-y-6">
                    {filteredCompanies.map(company => (
                        <div key={company.company_id} className="bg-white shadow rounded-lg p-6">
                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold">{company.company_name}</h3>
                                    <p className="text-sm text-gray-600">
                                        {__('Member Type:')} {company.member_type}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {__('Missing Years:')} {company.missing_years.sort((a,b) => b-a).join(', ')}
                                    </p>
                                </div>
                                <div>
                                    {/* Send reminder for whole company */}
                                    <button
                                        className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                        onClick={() => {
                                            company.users.forEach(user => 
                                                sendReminder(user.ID, selectedYear, company.company_name)
                                            );
                                        }}
                                    >
                                        {__('Send Company Reminder')}
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-6 py-3 text-left">{__('Username')}</th>
                                            <th className="px-6 py-3 text-left">{__('Email')}</th>
                                            <th className="px-6 py-3 text-left">{__('Full Name')}</th>
                                            <th className="px-6 py-3 text-left">{__('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {company.users.map(user => {
                                            const reminderKey = `${user.ID}-${selectedYear}`;
                                            const reminderResult = reminderStatus[reminderKey];
                                            
                                            return (
                                                <tr key={user.ID} className="border-t">
                                                    <td className="px-6 py-4">{user.username}</td>
                                                    <td className="px-6 py-4">{user.email}</td>
                                                    <td className="px-6 py-4">{user.name}</td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                                                            onClick={() => sendReminder(user.ID, selectedYear, company.company_name)}
                                                            disabled={reminderResult?.status === 'success'}
                                                        >
                                                            {__('Send Reminder')}
                                                        </button>
                                                        {reminderResult && (
                                                            <span className={`ml-2 text-sm ${
                                                                reminderResult.status === 'success' ? 'text-green-600' : 'text-red-600'
                                                            }`}>
                                                                {reminderResult.message}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>{__('All companies have submitted their contributions for this year.')}</p>
            )}
        </div>
    );
};

export default MissingContributions;